import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { setTokenAsync, setRemember } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Bridges deep-link OAuth callbacks from the OS into the Capacitor WebView.
 *
 * When Google auth finishes in external Chrome, AuthCallback.jsx redirects to
 * `com.notelosh.app://auth?token=<session_token>`. Android's intent-filter
 * brings the app to the foreground and Capacitor's App plugin fires
 * `appUrlOpen`. This hook picks up that URL, saves the token, and refreshes
 * the auth context so the user lands on /app already signed in.
 */
export default function NativeAuthBridge() {
  const navigate = useNavigate();
  const { refresh } = useAuth();

  useEffect(() => {
    if (!window.Capacitor?.isNativePlatform?.()) return;
    let sub;
    (async () => {
      const { App } = await import("@capacitor/app");
      sub = await App.addListener("appUrlOpen", async ({ url }) => {
        if (!url || !url.startsWith("com.notelosh.app://")) return;
        try {
          const parsed = new URL(url);
          const token = parsed.searchParams.get("token");
          if (token) {
            // Native is always "stay logged in" — a native app that logs you
            // out on every launch is a bug, not a feature. Await the native
            // Preferences write so the token is durable BEFORE we navigate.
            setRemember(true);
            await setTokenAsync(token);
            await refresh();
            navigate("/app", { replace: true });
          }
        } catch (_err) { /* ignore malformed deep-link */ }
      });
    })();
    return () => { sub?.remove?.(); };
  }, [navigate, refresh]);

  return null;
}
